/**
 * BatchProcessesDropdown
 *
 * Navbar popover showing recent batch processes with status indicator.
 * Follows the NotificationsMenu pattern for layout and offset calculation.
 */

import { useState, useRef, useEffect } from 'react';
import { Button, Center, Indicator, Popover, ScrollArea, Text, UnstyledButton, rem } from '@mantine/core';
import { IconListCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useBatchProcessesSharedSelector, selectors } from '../../state/store';
import { BatchProcessCard } from '../../../list/components/BatchProcessesList/BatchProcessCard';
import classes from './BatchProcessesDropdown.module.css';

const MAX_DROPDOWN_ITEMS = 5;

export function BatchProcessesDropdown() {
  const { t } = useTranslation( 'batchProcesses' );
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLButtonElement>( null );
  const [ isOpen, setIsOpen ] = useState( false );
  const [ popoverOffset, setPopoverOffset ] = useState({ crossAxis: 0, mainAxis: 0 });

  const indicatorStatus = useBatchProcessesSharedSelector( selectors.indicatorStatus );
  const sortedActiveProcesses = useBatchProcessesSharedSelector( selectors.newestFirstProcesses );
  const progressByProcessId = useBatchProcessesSharedSelector( s => s.progressByProcessId );
  const displayedProcesses = sortedActiveProcesses.slice( 0, MAX_DROPDOWN_ITEMS );

  useEffect(() => {
    const calculateOffset = () => {
      if ( buttonRef.current ) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const distanceToRightEdge = window.innerWidth - buttonRect.right;
        setPopoverOffset({ crossAxis: distanceToRightEdge, mainAxis: 10 });
      }
    };

    calculateOffset();
    window.addEventListener( 'resize', calculateOffset );

    return () => {
      window.removeEventListener( 'resize', calculateOffset );
    };
  }, []);

  const handleNavigateToProcess = ( processId: string ) => {
    setIsOpen( false );
    navigate( `/batch-processes/${processId}` );
  };

  const handleViewAll = () => {
    setIsOpen( false );
    navigate( '/batch-processes' );
  };

  const showIndicator = indicatorStatus !== 'idle';

  return (
    <Popover
      position="bottom-end"
      withArrow
      shadow="md"
      width={360}
      withinPortal={true}
      offset={popoverOffset}
      opened={isOpen}
      onChange={setIsOpen}
    >
      <Popover.Target>
        <UnstyledButton
          ref={buttonRef}
          style={{ marginTop: showIndicator ? '6px' : '0px' }}
          onClick={() => setIsOpen( !isOpen )}
        >
          <Center>
            {showIndicator ? (
              <Indicator
                processing={indicatorStatus === 'running'}
                color={indicatorStatus === 'running' ? 'blue' : 'red'}
                size={6}
              >
                <IconListCheck
                  className={`${classes.icon} ${isOpen ? classes.activeIcon : ''}`}
                  style={{ width: rem( 20 ), height: rem( 20 ) }}
                  stroke={1.5}
                />
              </Indicator>
            ) : (
              <IconListCheck
                className={`${classes.icon} ${isOpen ? classes.activeIcon : ''}`}
                style={{ width: rem( 20 ), height: rem( 20 ), marginBottom: '1px' }}
                stroke={1.5}
              />
            )}
          </Center>
        </UnstyledButton>
      </Popover.Target>

      <Popover.Dropdown className={classes.dropdown} p={0}>
        <div className={classes.header}>
          <Text size="xs" fw={500} px="xs" py={6}>
            {t( 'batchProcesses_dropdown_title' )}
          </Text>
        </div>

        <ScrollArea.Autosize mah={350} scrollbarSize={6} offsetScrollbars>
          <div className={classes.processesList}>
            {displayedProcesses.length > 0 ? (
              displayedProcesses.map( process => (
                <div key={process.id} className={classes.processItem}>
                  <BatchProcessCard
                    process={process}
                    projectedCount={progressByProcessId[process.id]?.projectedCount ?? null}
                    compact
                    onClick={() => handleNavigateToProcess( process.id )}
                    t={t}
                  />
                </div>
              ))
            ) : (
              <Text ta="center" py="md" size="xs" c="dimmed">
                {t( 'batchProcesses_dropdown_empty' )}
              </Text>
            )}
          </div>
        </ScrollArea.Autosize>

        <div className={classes.footer}>
          <Button fullWidth variant="subtle" size="xs" onClick={handleViewAll}>
            {t( 'batchProcesses_dropdown_seeAll' )}
          </Button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
